import { create } from "zustand";
import type { ComposerAttachment } from "@/lib/chat-attachments";

export interface ChatDraft {
	text: string;
	// メモリ上のみで保持し永続化しない。
	attachments: ComposerAttachment[];
}

interface ChatDraftState {
	drafts: Record<string, ChatDraft>;
	/** マウント中の入力欄へ挿入待ちのテキスト。入力欄が消費したら取り除く。 */
	pendingInserts: Record<string, string>;
	/**
	 * 撤回・送信失敗したメッセージの復元待ち内容。入力欄が消費したら取り除く。
	 * 送信失敗時は添付も戻すが、メッセージ撤回時はテキストのみ戻す (既知の制限)。
	 */
	pendingRestores: Record<string, ChatDraft>;
	setDraftText: (chatId: string, text: string) => void;
	setDraftAttachments: (chatId: string, attachments: ComposerAttachment[]) => void;
	clearDraft: (chatId: string) => void;
	/** 入力欄へ挿入するテキストをキューに積む。連続呼び出しは連結される。 */
	queueDraftInsert: (chatId: string, text: string) => void;
	/** 挿入待ちテキストを消費して取り除く。 */
	consumeDraftInsert: (chatId: string) => void;
	/** 撤回・送信失敗したメッセージの復元をキューに積む。連続呼び出しは後勝ちで上書きする。 */
	queueDraftRestore: (chatId: string, restore: string | ChatDraft) => void;
	/** 復元待ちテキストを消費して取り除く。 */
	consumeDraftRestore: (chatId: string) => void;
}

const EMPTY_DRAFT: ChatDraft = { text: "", attachments: [] };

export const useChatDraftStore = create<ChatDraftState>((set) => ({
	drafts: {},
	pendingInserts: {},
	pendingRestores: {},
	setDraftText: (chatId, text) =>
		set((state) => {
			const prev = state.drafts[chatId] ?? EMPTY_DRAFT;
			if (prev.text === text) return state;
			return { drafts: { ...state.drafts, [chatId]: { ...prev, text } } };
		}),
	setDraftAttachments: (chatId, attachments) =>
		set((state) => {
			const prev = state.drafts[chatId] ?? EMPTY_DRAFT;
			if (prev.attachments === attachments) return state;
			// 空配列は呼び出しごとに新しい参照で渡されうる (初期値のフォールバックなど) ため、
			// 参照比較だけでは常に更新扱いになり、触っていないチャットにまで空の draft
			// エントリが残ってしまう。長さ0同士は内容が同じとみなして更新をスキップする。
			if (prev.attachments.length === 0 && attachments.length === 0) return state;
			return { drafts: { ...state.drafts, [chatId]: { ...prev, attachments } } };
		}),
	clearDraft: (chatId) =>
		set((state) => {
			if (!(chatId in state.drafts)) return state;
			const next = { ...state.drafts };
			delete next[chatId];
			return { drafts: next };
		}),
	queueDraftInsert: (chatId, text) =>
		set((state) => ({
			pendingInserts: {
				...state.pendingInserts,
				[chatId]: (state.pendingInserts[chatId] ?? "") + text,
			},
		})),
	consumeDraftInsert: (chatId) =>
		set((state) => {
			if (!(chatId in state.pendingInserts)) return state;
			const next = { ...state.pendingInserts };
			delete next[chatId];
			return { pendingInserts: next };
		}),
	queueDraftRestore: (chatId, restore) =>
		set((state) => ({
			pendingRestores: {
				...state.pendingRestores,
				[chatId]: typeof restore === "string" ? { ...EMPTY_DRAFT, text: restore } : restore,
			},
		})),
	consumeDraftRestore: (chatId) =>
		set((state) => {
			if (!(chatId in state.pendingRestores)) return state;
			const next = { ...state.pendingRestores };
			delete next[chatId];
			return { pendingRestores: next };
		}),
}));
